import { useEffect } from "react";
import {
    Box,
    Dialog,
    DialogContent,
    FormControl,
    FormHelperText,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button as FoodsButton, FoodsIcon } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    JsonAttribute,
    JsonAttributeType,
    AggregationType,
    JsonAttributeWrite,
    useCreateOrUpdateJsonAttribute,
} from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

// Zod schema for JsonAttribute form validation
const JsonAttributeFormSchema = z.object({
    id: z.number().optional(),
    label: z.string().min(1, "Label is required"),
    identifier: z
        .string()
        .min(1, "Identifier is required")
        .regex(/^[a-z0-9_]+$/, "Identifier can only contain lowercase letters, numbers, and underscores."),
    entityClass: z.string().min(1, "'Applies To' field is required"),
    type: z.nativeEnum(JsonAttributeType, {
        message: "Type is required",
    }),
    aggregationType: z.nativeEnum(AggregationType).nullable().optional(),
});

type JsonAttributeFormData = z.infer<typeof JsonAttributeFormSchema>;

type JsonAttributeModalProps = {
    open: boolean;
    onClose: () => void;
    jsonAttribute?: JsonAttribute;
    onSuccess?: (attribute: JsonAttribute) => void;
};

const APPLICABLE_ENTITIES = [
    { label: "Product", value: JSON_ATTRIBUTE_ENTITY_CLASS.product },
    { label: "Ingredient", value: JSON_ATTRIBUTE_ENTITY_CLASS.ingredient },
    { label: "Company", value: JSON_ATTRIBUTE_ENTITY_CLASS.organization },
    { label: "User", value: JSON_ATTRIBUTE_ENTITY_CLASS.user },
];

export const JsonAttributeModal = (props: JsonAttributeModalProps) => {
    const { open, onClose, jsonAttribute, onSuccess } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const mutation = useCreateOrUpdateJsonAttribute();

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        watch,
    } = useForm<JsonAttributeFormData>({
        mode: "onChange",
        resolver: zodResolver(JsonAttributeFormSchema),
        defaultValues: {
            id: undefined,
            label: "",
            identifier: "",
            entityClass: "",
            type: undefined,
            aggregationType: AggregationType.NONE,
        },
    });

    // Watch the type field to show/hide aggregationType field
    const selectedType = watch("type");
    const showAggregationType = selectedType === JsonAttributeType.INTEGER || selectedType === JsonAttributeType.FLOAT;

    const onSubmitHandler: SubmitHandler<JsonAttributeFormData> = (values) => {
        const attributeWrite: JsonAttributeWrite = {
            id: jsonAttribute?.id,
            label: values.label,
            identifier: values.identifier,
            entityClass: values.entityClass,
            type: values.type,
            aggregationType: showAggregationType ? values.aggregationType : null,
        };

        mutation.mutate(attributeWrite, {
            onSuccess: (response) => {
                const savedAttribute = response.data;
                if (onSuccess) {
                    onSuccess(savedAttribute);
                }
                handleClose();
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    message: t("jsonAttribute.error.saveFailed", "Failed to save attribute. Please try again."),
                    type: "error",
                });
            },
        });
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    useEffect(() => {
        if (open) {
            const formData: JsonAttributeFormData = {
                id: jsonAttribute?.id,
                label: jsonAttribute?.label || "",
                identifier: jsonAttribute?.identifier || "",
                entityClass: jsonAttribute?.entityClass || "",
                type: jsonAttribute?.type ?? JsonAttributeType.STRING,
                aggregationType: jsonAttribute?.aggregationType ?? AggregationType.NONE,
            };
            reset(formData);
        }
    }, [open, jsonAttribute, reset]);

    const isEditing = !!jsonAttribute;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh", width: "638px" } }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {isEditing ? t("edit_attribute", "Edit Attribute") : t("add_attribute", "Add an Attribute")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ padding: "8px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            <DialogContent sx={{ padding: "32px", backgroundColor: "#FEFCFB", overflowY: "auto" }}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "32px" }}
                            >
                                {t("information", "Information")}
                            </Typography>
                            <Stack spacing="24px">
                                {/* Attribute Label */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("jsonAttribute.label", "Label")}* :
                                    </Typography>
                                    <Controller
                                        name="label"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder={t(
                                                    "jsonAttribute.label.placeholder",
                                                    "Enter attribute label",
                                                )}
                                                error={Boolean(errors.label)}
                                                helperText={errors.label?.message}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 400,
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Attribute Identifier */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("jsonAttribute.identifier", "Identifier")}* :
                                    </Typography>
                                    <Controller
                                        name="identifier"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder={t(
                                                    "jsonAttribute.identifier.placeholder",
                                                    "Enter attribute identifier",
                                                )}
                                                error={Boolean(errors.identifier)}
                                                helperText={errors.identifier?.message}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 400,
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Applies To */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("jsonAttribute.appliesTo", "Applies To")}* :
                                    </Typography>
                                    <Controller
                                        name="entityClass"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl
                                                fullWidth
                                                error={Boolean(errors.entityClass)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Select
                                                    {...field}
                                                    displayEmpty
                                                    sx={{
                                                        "& .MuiSelect-select": {
                                                            fontSize: "0.875rem",
                                                            fontWeight: 400,
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                        },
                                                    }}
                                                    MenuProps={{
                                                        sx: {
                                                            "& .MuiMenuItem-root": {
                                                                "&.Mui-selected": {
                                                                    backgroundColor: "primary.light",
                                                                },
                                                                "&.Mui-selected:hover": {
                                                                    backgroundColor: "primary.main",
                                                                    color: "primary.contrastText",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {t("jsonAttribute.appliesTo.placeholder", "Select entity type")}
                                                    </MenuItem>
                                                    {APPLICABLE_ENTITIES.map((entity) => (
                                                        <MenuItem key={entity.value} value={entity.value}>
                                                            {entity.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>{errors.entityClass?.message}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Box>

                                {/* Type */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("jsonAttribute.type", "Type")}* :
                                    </Typography>
                                    <Controller
                                        name="type"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl
                                                fullWidth
                                                error={Boolean(errors.type)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "8px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #E7E6E4",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Select
                                                    {...field}
                                                    displayEmpty
                                                    sx={{
                                                        "& .MuiSelect-select": {
                                                            fontSize: "0.875rem",
                                                            fontWeight: 400,
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                        },
                                                    }}
                                                    MenuProps={{
                                                        sx: {
                                                            "& .MuiMenuItem-root": {
                                                                "&.Mui-selected": {
                                                                    backgroundColor: "primary.light",
                                                                },
                                                                "&.Mui-selected:hover": {
                                                                    backgroundColor: "primary.main",
                                                                    color: "primary.contrastText",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {t("jsonAttribute.type.placeholder", "Select type")}
                                                    </MenuItem>
                                                    {Object.values(JsonAttributeType).map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {t(`jsonAttribute.types.${type}`, type)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>{errors.type?.message}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Box>

                                {/* Aggregation Type - Only show for integer and float types */}
                                {showAggregationType && (
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                        >
                                            {t("jsonAttribute.aggregationType", "Aggregation Type")} :
                                        </Typography>
                                        <Controller
                                            name="aggregationType"
                                            control={control}
                                            render={({ field }) => (
                                                <FormControl
                                                    fullWidth
                                                    error={Boolean(errors.aggregationType)}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "8px",
                                                            backgroundColor: "white",
                                                            border: "1px solid #E7E6E4",
                                                            "& fieldset": {
                                                                border: "none",
                                                            },
                                                            "&:hover fieldset": {
                                                                border: "none",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                border: "none",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Select
                                                        {...field}
                                                        displayEmpty
                                                        value={field.value || AggregationType.NONE}
                                                        sx={{
                                                            "& .MuiSelect-select": {
                                                                fontSize: "0.875rem",
                                                                fontWeight: 400,
                                                                lineHeight: "1.6em",
                                                                color: "#393A36",
                                                            },
                                                        }}
                                                        MenuProps={{
                                                            sx: {
                                                                "& .MuiMenuItem-root": {
                                                                    "&.Mui-selected": {
                                                                        backgroundColor: "rgba(0, 0, 0, 0.08)",
                                                                    },
                                                                    "&.Mui-selected:hover": {
                                                                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        {Object.values(AggregationType).map((aggregationType) => (
                                                            <MenuItem key={aggregationType} value={aggregationType}>
                                                                {t(
                                                                    `jsonAttribute.aggregationType.${aggregationType}`,
                                                                    aggregationType,
                                                                )}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText>{errors.aggregationType?.message}</FormHelperText>
                                                </FormControl>
                                            )}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </form>
            </DialogContent>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                    padding: "32px",
                    borderTop: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <FoodsButton
                    variant="outlined"
                    onClick={handleClose}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderColor: theme.palette.primary[600],
                        color: theme.palette.primary[600],
                        backgroundColor: "white",
                        "&:hover": {
                            borderColor: theme.palette.primary[600],
                            backgroundColor: "rgba(47, 203, 212, 0.04)",
                        },
                    })}
                >
                    {t("cancel", "Cancel")}
                </FoodsButton>
                <FoodsButton
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit(onSubmitHandler)}
                    disabled={!isValid || mutation.isLoading}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            color: "white !important",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {isEditing ? t("save", "Save") : t("add", "Add")}
                </FoodsButton>
            </Box>
        </Dialog>
    );
};
